import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Route, Users, Truck, Clock, AlertCircle, MapPin, Flag, ChevronRight, PlayCircle, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import DriverDetailModal from "@/components/DriverDetailModal";
import VehicleDetailModal from "@/components/VehicleDetailModal";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const cards = [
  { key: "trips_today",     label: "Heutige Fahrten",      icon: Route,  color: "text-blue-600", to: "/fahrten" },
  { key: "open_trips",      label: "Offene Fahrten",       icon: AlertCircle, color: "text-amber-600", to: "/wochenplanung" },
  { key: "active_drivers",  label: "Aktive Fahrer",        icon: Users,  color: "text-emerald-600", to: "/fahrer" },
  { key: "vehicles_in_use", label: "Fahrzeuge im Einsatz", icon: Truck,  color: "text-indigo-600", to: "/fahrzeuge" },
  { key: "active_shifts",   label: "Laufende Arbeitszeiten", icon: Clock, color: "text-cyan-600", to: "/arbeitszeit" },
];

const FAHRER_ALLOWED = ["/fahrten", "/arbeitszeit"];
const addr1 = (a) => {
  if (!a) return "—";
  const s = [a.street, a.house_number].filter(Boolean).join(" ");
  return [s, a.city].filter(Boolean).join(", ") || "—";
};
const firstPickup = (t) => (t.pickups && t.pickups[0]) || t.pickup_address;
const firstDest = (t) => (t.destinations && t.destinations[0]) || t.destination_address;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isFahrer = user.role === "fahrer";
  const [stats, setStats] = useState({});
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [detailDriverId, setDetailDriverId] = useState(null);
  const [detailVehicleId, setDetailVehicleId] = useState(null);
  const detailDriver = useMemo(() => drivers.find((d) => d.id === detailDriverId) || null, [drivers, detailDriverId]);
  const detailVehicle = useMemo(() => vehicles.find((v) => v.id === detailVehicleId) || null, [vehicles, detailVehicleId]);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/trips", { params: { date: new Date().toISOString().slice(0, 10) } }),
      ]);
      setStats(s.data);
      setRecent(t.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
    try {
      const [d, v] = await Promise.all([api.get("/drivers"), api.get("/vehicles")]);
      setDrivers(d.data); setVehicles(v.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (t, status) => {
    try { await api.patch(`/trips/${t.id}`, { status }); toast.success(`Status: ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const goTo = (to) => {
    if (isFahrer && !FAHRER_ALLOWED.includes(to)) return;
    navigate(to);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl heading font-semibold tracking-tight" data-testid="dashboard-title">Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Willkommen zurück, {user.full_name || user.username}.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          const clickable = !isFahrer || FAHRER_ALLOWED.includes(c.to);
          return (
            <Card
              key={c.key}
              className={`p-4 transition-all ${clickable ? "cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md" : ""}`}
              data-testid={`stat-${c.key}`}
              onClick={() => clickable && goTo(c.to)}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={(e) => { if (clickable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); goTo(c.to); } }}
            >
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</div>
                <Icon className={`h-4 w-4 ${c.color}`} />
              </div>
              <div className="mt-2 flex items-end justify-between">
                <div className="text-3xl font-semibold heading tabular-nums">
                  {loading ? "—" : (stats[c.key] ?? 0)}
                </div>
                {clickable && <ChevronRight className="h-4 w-4 text-muted-foreground mb-1" />}
              </div>
            </Card>
          );
        })}
      </div>

      {isFahrer ? (
        <MyTourToday trips={recent} loading={loading} setStatus={setStatus} onOpenVehicle={(id) => setDetailVehicleId(id)} />
      ) : (
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="text-base font-semibold heading">Heutige Fahrten</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate("/wochenplanung")} data-testid="dashboard-open-week">
              Zur Wochenplanung <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          {recent.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">Keine Fahrten für heute geplant.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">Zeit</th>
                    <th className="text-left px-4 py-2">Abholung</th>
                    <th className="text-left px-4 py-2">Ziel</th>
                    <th className="text-left px-4 py-2">Fahrer</th>
                    <th className="text-left px-4 py-2">Fahrzeug</th>
                    <th className="text-left px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((t) => (
                    <tr key={t.id} className="border-t border-border row-hover cursor-pointer" onClick={() => navigate("/fahrten")} data-testid={`dashboard-trip-${t.id}`}>
                      <td className="px-4 py-2 tabular-nums">{t.start_time}{t.end_time ? ` – ${t.end_time}` : ""}</td>
                      <td className="px-4 py-2 text-muted-foreground">{addr1(firstPickup(t))}</td>
                      <td className="px-4 py-2 text-muted-foreground">{addr1(firstDest(t))}</td>
                      <td className="px-4 py-2">{t.driver_id ? (
                        <button onClick={(e) => { e.stopPropagation(); setDetailDriverId(t.driver_id); }} className="text-blue-700 dark:text-blue-300 hover:underline" data-testid={`dashboard-driver-link-${t.id}`}>
                          {t.driver_name || "—"}
                        </button>
                      ) : (t.driver_name || "—")}</td>
                      <td className="px-4 py-2">{t.vehicle_id ? (
                        <button onClick={(e) => { e.stopPropagation(); setDetailVehicleId(t.vehicle_id); }} className="text-blue-700 dark:text-blue-300 hover:underline" data-testid={`dashboard-vehicle-link-${t.id}`}>
                          {t.license_plate || "—"}
                        </button>
                      ) : (t.license_plate || "—")}</td>
                      <td className="px-4 py-2"><StatusBadge status={t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <DriverDetailModal driver={detailDriver} open={!!detailDriver} onOpenChange={(o) => !o && setDetailDriverId(null)} />
      <VehicleDetailModal vehicle={detailVehicle} open={!!detailVehicle} onOpenChange={(o) => !o && setDetailVehicleId(null)} />
    </div>
  );
}

/* Mobile-friendly "Meine Tour heute" for drivers */
function MyTourToday({ trips, loading, setStatus, onOpenVehicle }) {
  const sorted = [...trips].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  return (
    <div className="space-y-3" data-testid="my-tour-today">
      <h2 className="text-lg font-semibold heading flex items-center gap-2"><Route className="h-5 w-5 text-blue-600" /> Meine Tour heute</h2>
      {loading ? (
        <Card className="p-6 text-sm text-muted-foreground">Lädt …</Card>
      ) : sorted.length === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground" data-testid="my-tour-empty">Heute keine Fahrten geplant. Genieß den Tag! 🎉</Card>
      ) : (
        sorted.map((t) => (
          <Card key={t.id} className="p-4 space-y-3" data-testid={`my-tour-trip-${t.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-base font-semibold tabular-nums">{t.start_time}{t.end_time ? `–${t.end_time}` : ""}</span>
              <StatusBadge status={t.status} />
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" /><span>{addr1(firstPickup(t))}</span></div>
              <div className="flex items-start gap-2"><Flag className="h-4 w-4 mt-0.5 text-blue-600 shrink-0" /><span>{addr1(firstDest(t))}</span></div>
              {(t.license_plate || t.line_label) && (
                <div className="flex items-center gap-2 text-muted-foreground"><Truck className="h-4 w-4 shrink-0" />
                  {t.vehicle_id ? (
                    <button onClick={() => onOpenVehicle(t.vehicle_id)} className="text-blue-700 dark:text-blue-300 hover:underline text-left" data-testid={`my-tour-vehicle-link-${t.id}`}>
                      {t.line_label ? `${t.line_label} · ` : ""}{t.license_plate || "—"}
                    </button>
                  ) : <span>{t.line_label ? `${t.line_label} · ` : ""}{t.license_plate || "—"}</span>}
                </div>
              )}
              {t.notes && <p className="text-xs text-muted-foreground pt-1">{t.notes}</p>}
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                variant={t.status === "unterwegs" ? "default" : "outline"}
                className="flex-1"
                onClick={() => setStatus(t, "unterwegs")}
                disabled={t.status === "abgeschlossen" || t.status === "storniert"}
                data-testid={`my-tour-start-${t.id}`}
              >
                <PlayCircle className="h-4 w-4 mr-1" /> Unterwegs
              </Button>
              <Button
                size="sm"
                variant={t.status === "abgeschlossen" ? "default" : "outline"}
                className="flex-1"
                onClick={() => setStatus(t, "abgeschlossen")}
                disabled={t.status === "storniert"}
                data-testid={`my-tour-done-${t.id}`}
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Abgeschlossen
              </Button>
            </div>
          </Card>
        ))
      )}
    </div>
  );
}
